import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Zap, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TEINTES = [
  { fond: '#E1F5EE', trait: '#1D9E75', texte: '#0F6E56', rvb: '29,158,117' },
  { fond: '#EEEDFE', trait: '#7F77DD', texte: '#3C3489', rvb: '127,119,221' },
  { fond: '#FAECE7', trait: '#D85A30', texte: '#993C1D', rvb: '216,90,48' },
  { fond: '#FBEAF0', trait: '#D4537E', texte: '#993556', rvb: '212,83,126' },
  { fond: '#E6F1FB', trait: '#378ADD', texte: '#185FA5', rvb: '55,138,221' },
  { fond: '#FAEEDA', trait: '#EF9F27', texte: '#854F0B', rvb: '239,159,39' },
  { fond: '#EAF3DE', trait: '#97C459', texte: '#3B6D11', rvb: '151,196,89' },
  { fond: '#F1EFE8', trait: '#888780', texte: '#444441', rvb: '136,135,128' },
];

const LARG = 46, ECART = 12, PAS = 58;

const TechRaccordement = ({ chantierId, verrouille = false, onEnregistre, bloque, messageBloque, cleMaj }) => {
  const [donnees, setDonnees] = useState(null);
  const [rangees, setRangees] = useState([]);
  const [liaisons, setLiaisons] = useState({});
  const [choisi, setChoisi] = useState(null);
  const [parMicro, setParMicro] = useState(2);
  const [tape, setTape] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/chantiers/${chantierId}/raccordement`)
      .then((r) => {
        const d = r.data.data;
        setDonnees(d);
        setRangees(d.rangees.length ? d.rangees : [d.nb_panneaux]);
        setLiaisons(d.liaisons || {});
        setParMicro(d.panneaux_par_micro || 2);
      })
      .catch(() => setDonnees(null));
  }, [chantierId, cleMaj]);

  useEffect(() => {
    if (verrouille) return;
    if (!donnees || !rangees.length) return;
    const t = setTimeout(() => {
      axios.post(`${API}/api/chantiers/${chantierId}/raccordement`, {
        rangees, liaisons, panneaux_par_micro: parMicro
      })
        .then(() => { if (onEnregistre) onEnregistre(); })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [liaisons, rangees, parMicro, donnees, verrouille, chantierId]);

  if (!donnees) return null;

  if (bloque) {
    return (
      <div style={{ opacity: 0.55 }}>
        <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
          {messageBloque || 'Disponible plus tard'}
        </div>
      </div>
    );
  }

  const total = donnees.nb_panneaux;
  const micros = donnees.micro_onduleurs || [];
  const teinte = (code) => TEINTES[micros.indexOf(code) % TEINTES.length];
  const proprio = (n) => Object.keys(liaisons).find((c) => (liaisons[c] || []).includes(n));
  const libres = micros.filter((c) => !(liaisons[c] || []).length);

  const repartir = (nb) => {
    const base = Math.floor(total / nb), reste = total % nb;
    return Array.from({ length: nb }, (_, i) => base + (i < reste ? 1 : 0));
  };

  const changerRangees = (nb) => {
    setRangees(repartir(nb));
    setLiaisons({});
    setChoisi(null);
  };

  const changerTaille = (index, valeur) => {
    const suite = [...rangees];
    suite[index] = valeur;
    let restant = total - valeur;
    const autres = suite.map((_, i) => i).filter((i) => i !== index);
    autres.forEach((i, k) => {
      const part = Math.max(1, Math.floor(restant / (autres.length - k)));
      suite[i] = part;
      restant -= part;
    });
    if (restant !== 0 && autres.length) suite[autres[autres.length - 1]] += restant;
    setRangees(suite);
    setLiaisons({});
    setChoisi(null);
  };

  const toucher = (n) => {
    if (verrouille) return;
    setTape(n);
    setTimeout(() => setTape(null), 850);

    const dejaLa = proprio(n);
    if (dejaLa) {
      const suite = { ...liaisons };
      delete suite[dejaLa];
      setLiaisons(suite);
      setChoisi(null);
      return;
    }
    if (choisi === null) return;

    const actuels = [...(liaisons[choisi] || [])];
    if (actuels.length >= parMicro) actuels.shift();
    actuels.push(n);
    actuels.sort((a, b) => a - b);
    setLiaisons({ ...liaisons, [choisi]: actuels });
    if (actuels.length === parMicro) setChoisi(null);
  };

  const enregistrer = async () => {
    setEnCours(true);
    setMessage('');
    try {
      await axios.post(`${API}/api/chantiers/${chantierId}/raccordement`, { rangees, liaisons, panneaux_par_micro: parMicro });
      setMessage('Raccordement enregistré');
      if (onEnregistre) onEnregistre();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setEnCours(false);
    }
  };

  const tousRaccordes = micros.length > 0 && libres.length === 0;

  return (
    <>
      <style>{`@keyframes haloPanneau {
        0% { box-shadow: 0 0 0 0 var(--tf); }
        70% { box-shadow: 0 0 0 12px var(--tn); }
        100% { box-shadow: 0 0 0 0 var(--tn); }
      }`}</style>

      {micros.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '8px 0' }}>
          Scannez d'abord les micro-onduleurs
        </div>
      ) : (
        <>
          {!verrouille && (
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', color: '#9CA3AF', letterSpacing: '.4px' }}>MICRO-ONDULEURS</span>
                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                  {libres.length ? `${libres.length} restant${libres.length > 1 ? 's' : ''}` : 'tous raccordés'}
                </span>
              </div>
              {libres.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '8px 2px' }}>Tous raccordés</div>
              ) : micros.map((code) => {
                if ((liaisons[code] || []).length) return null;
                const t = teinte(code);
                const actif = choisi === code;
                const premierLibre = micros.find((c) => !(liaisons[c] || []).length);
                const bloque = code !== premierLibre;
                return (
                  <div key={code} onClick={() => { if (!bloque) setChoisi(actif ? null : code); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 14px',
                      borderRadius: '10px', fontSize: '13px', cursor: 'pointer', marginBottom: '7px',
                      background: actif ? t.fond : '#F9FAFB',
                      transform: actif ? 'translateX(4px)' : 'none',
                      transition: 'all .18s',
                      opacity: bloque ? 0.4 : 1,
                      cursor: bloque ? 'default' : 'pointer'
                    }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, background: actif ? t.trait : '#D1D5DB' }} />
                    <span style={{ fontFamily: 'monospace', flex: 1, color: actif ? t.texte : '#6B7280' }}>{code}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', color: '#9CA3AF', letterSpacing: '.4px' }}>TOITURE</span>
            <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{total} panneaux</span>
          </div>

          {!verrouille && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '5px' }}>Nombre de rangées</div>
              <select value={rangees.length} onChange={(e) => changerRangees(+e.target.value)}
                style={{ width: '100%', height: '38px', border: '1px solid #E5E7EB', borderRadius: '9px', padding: '0 12px', fontSize: '14px', background: '#fff' }}>
                {Array.from({ length: Math.min(total, 5) }, (_, i) => i + 1).map((v) => (
                  <option key={v} value={v}>{v} rangée{v > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          )}

          {(() => {
            let num = 0;
            return rangees.map((nb, ri) => {
              const debut = num + 1;
              const cases = [];
              for (let k = 0; k < nb; k++) {
                num++;
                const idx = num;
                const code = proprio(idx);
                const t = code ? teinte(code) : null;
                const rvb = t ? t.rvb : (choisi ? teinte(choisi).rvb : '107,114,128');
                cases.push(
                  <div key={idx} onClick={() => toucher(idx)}
                    style={{
                      width: `${LARG}px`, height: '64px', flexShrink: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                      borderRadius: '5px', cursor: verrouille ? 'default' : 'pointer',
                      background: t ? t.fond : '#F9FAFB',
                      border: `1.5px solid ${t ? t.trait : '#E5E7EB'}`,
                      color: t ? t.texte : '#9CA3AF',
                      transition: 'all .2s cubic-bezier(.3,1,.4,1)',
                      '--tf': `rgba(${rvb},.42)`, '--tn': `rgba(${rvb},0)`,
                      animation: tape === idx ? 'haloPanneau .8s ease-out' : 'none'
                    }}>
                    {idx}
                  </div>
                );
              }

              const barres = [];
              Object.keys(liaisons).forEach((code) => {
                const dans = (liaisons[code] || []).filter((x) => x >= debut && x < debut + nb);
                if (!dans.length) return;
                const a = Math.min(...dans), b = Math.max(...dans);
                barres.push(
                  <div key={code} style={{
                    position: 'absolute', left: `${(a - debut) * PAS}px`,
                    width: `${(b - a) * PAS + LARG}px`, height: '3px',
                    borderRadius: '99px', background: teinte(code).trait
                  }} />
                );
              });

              return (
                <div key={ri} style={{ position: 'relative', padding: '10px 0 16px' }}>
                  {rangees.length > 1 && !verrouille && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '4px', padding: '0 8px' }}>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Rangée {ri + 1}</span>
                      <select value={nb} onChange={(e) => changerTaille(ri, +e.target.value)}
                        style={{ height: '32px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '0 10px', fontSize: '13px', background: '#fff' }}>
                        {Array.from({ length: Math.min(12, total - (rangees.length - 1)) }, (_, i) => i + 1).map((v) => (
                          <option key={v} value={v}>{v} panneau{v > 1 ? 'x' : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: `${ECART}px`, overflowX: 'auto', padding: '8px 8px 4px' }}>
                    {cases}
                  </div>
                  <div style={{ position: 'absolute', left: '8px', right: '8px', bottom: '6px', height: '3px', pointerEvents: 'none' }}>
                    {barres}
                  </div>
                </div>
              );
            });
          })()}

          {false && (
            <>
              {message && (
                <div style={{ fontSize: '13px', color: message.includes('enregistr') ? '#065F46' : '#991B1B', marginBottom: '10px' }}>
                  {message}
                </div>
              )}
              <button onClick={enregistrer} disabled={enCours || !tousRaccordes}
                style={{
                  width: '100%', padding: '13px', border: 'none', borderRadius: '9px',
                  background: tousRaccordes ? '#10B981' : '#F3F4F6',
                  color: tousRaccordes ? '#fff' : '#9CA3AF',
                  fontSize: '15px', cursor: tousRaccordes ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}>
                <Check size={17} />
                {enCours ? 'Enregistrement…' : tousRaccordes ? 'Enregistrer le raccordement' : `${libres.length} micro-onduleur${libres.length > 1 ? 's' : ''} à raccorder`}
              </button>
            </>
          )}
        </>
      )}
    </>
  );
};

export default TechRaccordement;
